import { PaymentApiClient } from '@/clients/paymentApiClient';
import {
  useScaChallengePolling,
  type ScaChallengeInitialData,
} from '@/hooks/useScaChallengeState';

/**
 * Polls payment (signing basket) authorization status. All the SCA-challenge machinery lives in
 * useScaChallengePolling, shared with useConsentPolling — see that file for why, including the
 * real bug the previous duplication caused on exactly this hook.
 */
export const usePaymentPolling = (
  signingBasketId: string,
  authorisationId: string,
  xRequestId: string,
  bicFi: string,
  initialData: ScaChallengeInitialData
) =>
  useScaChallengePolling(initialData, {
    enabled: !!signingBasketId && !!bicFi,
    defaultIntervalMs: 600,
    label: 'payment',
    arrivalTimeoutMessage:
      'We couldn’t confirm your payment authorization within the expected time. Please try again.',
    arrivalTimeoutDescription: `Payment polling: no completion signal for signingBasketId=${signingBasketId}`,
    arrivalTimeoutContext: 'usePaymentPolling: arrival timeout',
    fetchStatus: async () => {
      const status = await new PaymentApiClient().getAuthorisationStatus(
        signingBasketId,
        authorisationId,
        xRequestId,
        bicFi,
      );
      // PIS reports completion as `completed`; AIS as `isCompleted`. Normalised here rather than
      // teaching the shared hook about both spellings. `needsReview` (FIN-968): the bank finalised
      // the signature but a signed payment is no longer linked to an open invoice — terminal, and
      // never shown as a completed payment.
      return {
        ...status,
        isCompleted: status.completed,
        isFailed: status.needsReview === true,
        failedMessage: status.needsReview ? status.message : undefined,
      };
    },
    identityKey: [signingBasketId, authorisationId, xRequestId, bicFi].join('|'),
  });
